import React from 'react';
import { X, Copy, Check, Download } from 'lucide-react';
import { Button } from '@shared/components/ui/Button';
import { Modal } from '@shared/components/ui/Modal';

interface ExtractedTextPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentName: string;
  extractedText: string;
  originalText?: string;
  showComparison?: boolean;
  extractionMethod?: string;
  qualityScore?: number;
  ocrUsed?: boolean;
  ocrConfidence?: number;
}

const ExtractedTextPreviewModal: React.FC<ExtractedTextPreviewModalProps> = ({
  isOpen,
  onClose,
  documentName,
  extractedText,
  originalText,
  showComparison = false,
  extractionMethod,
  qualityScore,
  ocrUsed,
  ocrConfidence
}) => {
  const [copied, setCopied] = React.useState(false);
  const [viewMode, setViewMode] = React.useState<'cleaned' | 'original' | 'comparison'>(
    showComparison && originalText ? 'comparison' : 'cleaned'
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(extractedText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy text:', error);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([extractedText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${documentName}_extracted.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getExtractionMethodBadge = () => {
    if (!extractionMethod) return null;

    const badges = {
      'pdfjs_text_content': { label: 'PDF.js', color: 'bg-blue-100 text-blue-800' },
      'pdfjs_text_layer': { label: 'PDF.js Layer', color: 'bg-blue-100 text-blue-800' },
      'fallback_text_extraction': { label: 'Fallback', color: 'bg-yellow-100 text-yellow-800' },
      'pdfco_api': { label: 'PDF.co', color: 'bg-green-100 text-green-800' },
      'ocr': { label: 'OCR', color: 'bg-purple-100 text-purple-800' }
    };

    const badge = badges[extractionMethod as keyof typeof badges] || { label: extractionMethod, color: 'bg-gray-100 text-gray-800' };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  const getQualityBadge = () => {
    if (qualityScore === undefined || qualityScore === null) return null;

    let color = 'bg-gray-100 text-gray-800';
    if (qualityScore >= 80) color = 'bg-green-100 text-green-800';
    else if (qualityScore >= 60) color = 'bg-yellow-100 text-yellow-800';
    else color = 'bg-red-100 text-red-800';

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${color}`}>
        Quality: {qualityScore}/100
      </span>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Preview: ${documentName}`} size="xl">
      <div className="space-y-4">
        {/* Metadata Bar */}
        <div className="flex flex-wrap items-center gap-2 pb-3 border-b border-gray-200">
          {getExtractionMethodBadge()}
          {getQualityBadge()}
          {ocrUsed && (
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
              📷 OCR {ocrConfidence ? `(${ocrConfidence.toFixed(0)}%)` : ''}
            </span>
          )}
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
            {extractedText.length.toLocaleString()} characters
          </span>
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
            {extractedText.split(/\s+/).filter(w => w.length > 0).length.toLocaleString()} words
          </span>
        </div>

        {/* View Mode Tabs */}
        {showComparison && originalText && (
          <div className="flex gap-2 border-b border-gray-200">
            <button
              onClick={() => setViewMode('cleaned')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                viewMode === 'cleaned'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Cleaned Text
            </button>
            <button
              onClick={() => setViewMode('original')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                viewMode === 'original'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Original Text
            </button>
            <button
              onClick={() => setViewMode('comparison')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                viewMode === 'comparison'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Side by Side
            </button>
          </div>
        )}

        {/* Text Content */}
        <div className="relative">
          {viewMode === 'comparison' && originalText ? (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Original</h4>
                <div className="bg-gray-50 rounded-md p-4 h-96 overflow-y-auto border border-gray-200">
                  <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono leading-relaxed">
                    {originalText}
                  </pre>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Cleaned</h4>
                <div className="bg-green-50 rounded-md p-4 h-96 overflow-y-auto border border-green-200">
                  <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono leading-relaxed">
                    {extractedText}
                  </pre>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-md p-4 max-h-96 overflow-y-auto border border-gray-200">
              <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono leading-relaxed">
                {viewMode === 'original' && originalText ? originalText : extractedText}
              </pre>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center pt-3 border-t border-gray-200">
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleCopy}
              className="flex items-center gap-2"
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  Copy Text
                </>
              )}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleDownload}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Download
            </Button>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={onClose}
            className="flex items-center gap-2"
          >
            <X className="h-4 w-4" />
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ExtractedTextPreviewModal;
